import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save, RotateCcw, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { companyService } from '@/services/companyService';
import { triggerReloadWithSettings, pickReloadSearch } from '@/utils/reloadSearch';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';

const PURCHASE_CATEGORY: CategoryKey = 'purchase';

interface RuleSetting {
  id?: string;
  rule_key: string;
  rule_value: number;
  is_active: boolean;
}

interface RuleSettingsData {
  serviceLevel: RuleSetting;
  leadTime: RuleSetting;
  leadTimeStd: RuleSetting;
  bufferDays: RuleSetting;
}

const createDefaultSettings = (): RuleSettingsData => ({
  serviceLevel: { rule_key: 'service_level', rule_value: 95, is_active: true },
  leadTime: { rule_key: 'lead_time', rule_value: 7, is_active: true },
  leadTimeStd: { rule_key: 'lead_time_std', rule_value: 1, is_active: true },
  bufferDays: { rule_key: 'buffer_days', rule_value: 21, is_active: true },
});

const RuleSettings = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [settings, setSettings] = useState<RuleSettingsData>(() => createDefaultSettings());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const loadSettings = async () => {
    try {
      setLoading(true);
      await ensureAuthReady();

      // Get current company
      const currentCompany = await companyService.getCurrentCompany();

      if (!currentCompany) {
        toast.error(language === 'zh' ? '請先選擇公司' : 'Please select a company first');
        return;
      }

      const companyId = currentCompany.id;

      // Load existing settings
      const { data: rulesData, error: rulesError } = await supabase
        .from('purchase_rules')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (rulesError) {
        console.error('Error loading settings:', rulesError);
        toast.error(language === 'zh' ? '載入設定失敗' : 'Failed to load settings');
        return;
      }

      // Update settings with loaded data
      const newSettings = createDefaultSettings();
      const resolveRuleValue = (value: any, fallback: number): number => {
        if (typeof value === 'number') {
          return value;
        }
        if (typeof value === 'string') {
          const parsed = Number(value);
          return Number.isNaN(parsed) ? fallback : parsed;
        }
        if (value && typeof value === 'object') {
          if ('value' in value) {
            const nestedValue = Number((value as { value: unknown }).value);
            return Number.isNaN(nestedValue) ? fallback : nestedValue;
          }
          const numericValue = Number(value);
          return Number.isNaN(numericValue) ? fallback : numericValue;
        }
        return fallback;
      };

      rulesData?.forEach((rule: any) => {
        switch (rule.rule_key) {
          case 'service_level':
            newSettings.serviceLevel = {
              ...newSettings.serviceLevel,
              id: rule.id,
              rule_value: resolveRuleValue(rule.rule_value, newSettings.serviceLevel.rule_value),
              is_active: rule.is_active,
            };
            break;
          case 'lead_time':
            newSettings.leadTime = {
              ...newSettings.leadTime,
              id: rule.id,
              rule_value: resolveRuleValue(rule.rule_value, newSettings.leadTime.rule_value),
              is_active: rule.is_active,
            };
            break;
          case 'lead_time_std':
            newSettings.leadTimeStd = {
              ...newSettings.leadTimeStd,
              id: rule.id,
              rule_value: resolveRuleValue(rule.rule_value, newSettings.leadTimeStd.rule_value),
              is_active: rule.is_active,
            };
            break;
          case 'buffer_days':
            newSettings.bufferDays = {
              ...newSettings.bufferDays,
              id: rule.id,
              rule_value: resolveRuleValue(rule.rule_value, newSettings.bufferDays.rule_value),
              is_active: rule.is_active,
            };
            break;
        }
      });

      setSettings(newSettings);
      setHasChanges(false);
    } catch (error) {
      console.error('Error in loadSettings:', error);
      toast.error(language === 'zh' ? '載入設定時發生錯誤' : 'Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  // 首次載入時檢查是否有待處理的設定儲存
  useEffect(() => {
    const pending = pickReloadSearch();

    // 檢查是否有待處理的設定儲存
    if (!pending || !pending.filters?.settings || pending.filters?.source !== 'settings_save') return;

    console.log('🔄 檢測到待處理的設定儲存，開始恢復設定並儲存');

    // 恢復設定到UI狀態
    setSettings(pending.filters.settings);

    // 執行儲存操作
    const executePendingSave = async () => {
    try {
        await ensureAuthReady();

        // 重新獲取公司資訊（因為頁面重新載入後可能需要重新初始化）
      const currentCompany = await companyService.getCurrentCompany();
      if (!currentCompany) {
        toast.error(language === 'zh' ? '請先選擇公司' : 'Please select a company first');
        return;
      }

      const companyId = currentCompany.id;

        // 準備設定來儲存
        const settingsToSave = Object.values(pending.filters.settings).map((setting: any) => ({
        company_id: companyId,
        rule_key: setting.rule_key,
        rule_value: setting.rule_value,
        is_active: true
      }));

        // 先嘗試更新現有記錄
        const updatePromises = settingsToSave.map(async (setting: any) => {
        const { error } = await supabase
          .from('purchase_rules')
          .update({
            rule_value: setting.rule_value,
            is_active: true
          })
          .eq('company_id', companyId)
          .eq('rule_key', setting.rule_key);

        return { setting, error, operation: 'update' };
      });

      const updateResults = await Promise.all(updatePromises);

        // 檢查是否有更新失敗
      const failedUpdates = updateResults.filter(result => result.error);
      if (failedUpdates.length > 0) {
        console.error('Update errors:', failedUpdates);
        toast.error(language === 'zh' ? `更新設定失敗: ${failedUpdates[0].error.message}` : `Failed to update settings: ${failedUpdates[0].error.message}`);
        return;
      }

        // 檢查有多少記錄被實際更新
      const { count: updatedCount } = await supabase
        .from('purchase_rules')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId)
          .in('rule_key', settingsToSave.map((s: any) => s.rule_key));

        // 如果有些記錄不存在，插入它們
      if (updatedCount < settingsToSave.length) {
          const insertPromises = settingsToSave.map(async (setting: any) => {
            // 檢查這個設定是否已經存在
          const { data: existing } = await supabase
            .from('purchase_rules')
            .select('id')
            .eq('company_id', companyId)
            .eq('rule_key', setting.rule_key)
            .eq('is_active', true)
            .single();

          if (!existing) {
            const { error } = await supabase
              .from('purchase_rules')
              .insert(setting);
            return { setting, error, operation: 'insert' };
          }
          return { setting, error: null, operation: 'skip' };
        });

        const insertResults = await Promise.all(insertPromises);
        const failedInserts = insertResults.filter(result => result.error && result.operation === 'insert');

        if (failedInserts.length > 0) {
          console.error('Insert errors:', failedInserts);
          toast.error(language === 'zh' ? `插入設定失敗: ${failedInserts[0].error.message}` : `Failed to insert settings: ${failedInserts[0].error.message}`);
          return;
        }
      }

      setHasChanges(false);

        // ✅ 儲存成功後，直接重新載入設定數據驗證更新並顯示最終成功訊息
        try {
          console.log('🔄 設定儲存成功，重新載入設定數據驗證更新');
          await loadSettings(); // 重新載入設定數據
          toast.success(language === 'zh' ? '設定已儲存並更新完成' : 'Settings saved and updated successfully');
        } catch (error) {
          console.error('重新載入設定數據失敗:', error);
          // 如果重新載入失敗，仍顯示成功訊息，因為儲存操作已經成功
          toast.success(language === 'zh' ? '設定已儲存' : 'Settings saved successfully');
        }

    } catch (error) {
        console.error('回放設定儲存失敗:', error);
        toast.error(language === 'zh' ? '設定儲存恢復失敗，請重新儲存' : 'Settings save restore failed, please save again');
      }
    };

    executePendingSave();
  }, [language]); // 移除其他依賴避免無限循環

  const updateSetting = (key: keyof RuleSettingsData, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        rule_value: value
      }
    }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    // 🔄 使用專業的重新連接儲存機制
    console.log('🔄 觸發專業重新連接儲存機制，將設定保存到 sessionStorage 並重新載入頁面');
    triggerReloadWithSettings(settings, 'settings_save');
  };

  const resetToDefaults = () => {
    setSettings(createDefaultSettings());
    setHasChanges(true);
    toast.info(language === 'zh' ? '已重置為預設值' : 'Reset to default values');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-400">
                {language === 'zh' ? '載入設定中...' : 'Loading settings...'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              localStorage.setItem('selectedCategory', PURCHASE_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'zh' ? '返回' : 'Back'}
          </Button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {language === 'zh' ? '細則內容' : 'Rule Settings'}
            </h1>
            <p className="text-gray-400 mt-1">
              {language === 'zh' ? '設定採購建議計算參數' : 'Configure purchase suggestion calculation parameters'}
            </p>
          </div>
        </div>
      </div>

      {/* Formula Display */}
      <Card className="mb-6 bg-card/80 border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Info className="h-5 w-5" />
            {language === 'zh' ? '安全存量計算公式' : 'Safety Stock Calculation Formula'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="text-center">
              <div className="text-2xl font-mono text-blue-400 mb-2">
                SS = Z√(Lσ_d² + d_avg²σ_L²)
              </div>
              <div className="text-sm text-muted-foreground">
                {language === 'zh'
                  ? '安全存量 = 服務水平因子 × √(提前期 × 需求方差 + 平均需求² × 提前期方差)'
                  : 'Safety Stock = Service Level Factor × √(Lead Time × Demand Variance + Average Demand² × Lead Time Variance)'
                }
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Editable Settings */}
      <Card className="mb-6 bg-card/80 border-border">
        <CardHeader>
          <CardTitle className="text-foreground">
            {language === 'zh' ? '可修改參數' : 'Editable Parameters'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
        {/* Service Level */}
        <div className="space-y-3">
          <Label className="text-foreground">
            {language === 'zh' ? '服務水準或Z值' : 'Service Level / Z Value'}
            <Badge variant="secondary" className="ml-2">
              {settings.serviceLevel.rule_value}%
            </Badge>
          </Label>
          <Input
            type="number"
            value={settings.serviceLevel.rule_value}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 0;
              if (value >= 1 && value <= 100) {
                updateSetting('serviceLevel', value);
              }
            }}
            min="1"
            max="100"
            className="bg-background/50 border-input text-foreground"
            placeholder="1-100"
          />
          <div className="text-sm text-muted-foreground">
            {language === 'zh' ? '範圍：1-100%' : 'Range: 1-100%'}
          </div>
        </div>

          {/* Lead Time */}
          <div className="space-y-3">
            <Label className="text-foreground">
              {language === 'zh' ? '提前期' : 'Lead Time'}
              <Badge variant="secondary" className="ml-2">
                {settings.leadTime.rule_value} {language === 'zh' ? '天' : 'days'}
              </Badge>
            </Label>
            <Input
              type="number"
              value={settings.leadTime.rule_value}
              onChange={(e) => updateSetting('leadTime', parseInt(e.target.value) || 0)}
              min="1"
              className="bg-background/50 border-input text-foreground"
            />
          </div>

          {/* Lead Time Standard Deviation */}
          <div className="space-y-3">
            <Label className="text-foreground">
              {language === 'zh' ? '提前期標準差' : 'Lead Time Standard Deviation'}
              <Badge variant="secondary" className="ml-2">
                {settings.leadTimeStd.rule_value} {language === 'zh' ? '天' : 'days'}
              </Badge>
            </Label>
            <Input
              type="number"
              value={settings.leadTimeStd.rule_value}
              onChange={(e) => updateSetting('leadTimeStd', parseFloat(e.target.value) || 0)}
              min="0"
              step="0.1"
              className="bg-background/50 border-input text-foreground"
            />
          </div>

          {/* Buffer Days */}
          <div className="space-y-3">
            <Label className="text-foreground">
              {language === 'zh' ? '預備貨品的存量的天數' : 'Buffer Stock Days'}
              <Badge variant="secondary" className="ml-2">
                {settings.bufferDays.rule_value} {language === 'zh' ? '天' : 'days'}
              </Badge>
            </Label>
            <Input
              type="number"
              value={settings.bufferDays.rule_value}
              onChange={(e) => updateSetting('bufferDays', parseInt(e.target.value) || 0)}
              min="1"
              className="bg-background/50 border-input text-foreground"
            />
          </div>
        </CardContent>
      </Card>

      {/* Information Reference */}
      <Card className="mb-6 bg-card/80 border-border">
        <CardHeader>
          <CardTitle className="text-foreground">
            {language === 'zh' ? '資訊參考' : 'Information Reference'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <h4 className="text-foreground font-medium mb-2">
                {language === 'zh' ? '平均日需求' : 'Average Daily Demand'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {language === 'zh'
                  ? '按每件產品最近90日的銷售記錄作計算平均值'
                  : 'Calculated from the last 90 days of sales records for each product'
                }
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <h4 className="text-foreground font-medium mb-2">
                {language === 'zh' ? '日需求標準差' : 'Daily Demand Std Dev'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {language === 'zh'
                  ? '按每件產品最近90日的銷售記錄作計算標準差的值'
                  : 'Standard deviation calculated from the last 90 days of sales records'
                }
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <h4 className="text-foreground font-medium mb-2">
                {language === 'zh' ? '目前庫存狀態' : 'Current Stock Status'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {language === 'zh'
                  ? '系統在supabase內的product_inventory找到該產品的company_sku_key，再查出現存數值'
                  : 'System queries product_inventory table in Supabase using company_sku_key'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <Button
          variant="outline"
          onClick={resetToDefaults}
          disabled={saving}
          className="flex items-center gap-2 bg-card/50 border-border text-foreground hover:bg-secondary/50"
        >
          <RotateCcw className="h-4 w-4" />
          {language === 'zh' ? '重置為預設' : 'Reset to Default'}
        </Button>

        <Button
          onClick={saveSettings}
          disabled={saving || !hasChanges}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Save className="h-4 w-4" />
          {saving
            ? (language === 'zh' ? '儲存中...' : 'Saving...')
            : (language === 'zh' ? '儲存設定' : 'Save Settings')
          }
        </Button>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <Alert className="mt-4 bg-yellow-500/10 border-yellow-500/50">
          <AlertDescription className="text-yellow-600 dark:text-yellow-400">
            {language === 'zh'
              ? '您有未儲存的變更。請記得儲存設定。'
              : 'You have unsaved changes. Remember to save your settings.'
            }
          </AlertDescription>
        </Alert>
      )}
      </div>
    </div>
  );
};

export default RuleSettings;
